import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ChevronDown, ChevronUp, RefreshCw, Edit, Trash2, Plus, Award, TrendingDown, Sparkles, IndianRupee } from 'lucide-react';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const monthLabel = (m) => {
  if (!m || m.length < 7) return m;
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const buildMonths = () => {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

export default function AdminEarningsOverview() {
  const months = buildMonths();
  const [month, setMonth] = useState(months[0]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedTeacher, setExpandedTeacher] = useState(null);
  const [teacherDetail, setTeacherDetail] = useState({});
  const [recomputing, setRecomputing] = useState(false);

  // Rate cards
  const [rates, setRates] = useState([]);
  const [editingRate, setEditingRate] = useState(null);
  const [rateForm, setRateForm] = useState({});

  // Milestones
  const [milestones, setMilestones] = useState([]);
  const [milestoneOpen, setMilestoneOpen] = useState(false);

  // Manual adjustment
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ teacher_id: '', month: '', type: 'incentive', label: '', amount_inr: 0, note: '' });

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/earnings/teachers?month=${month}`);
      setOverview(res.data);
    } catch {
      toast.error('Failed to load earnings overview');
    } finally {
      setLoading(false);
    }
  }, [month]);

  const fetchRates = useCallback(async () => {
    try {
      const res = await api.get('/admin/earnings/rates');
      setRates(res.data.rates || []);
    } catch {}
  }, []);

  const fetchMilestones = useCallback(async () => {
    try {
      const res = await api.get('/admin/earnings/milestones');
      setMilestones(res.data.milestones || []);
    } catch {}
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchRates(); fetchMilestones(); }, [fetchRates, fetchMilestones]);

  const fetchTeacherDetail = async (teacherId) => {
    try {
      const res = await api.get(`/admin/earnings/teacher/${teacherId}?month=${month}`);
      setTeacherDetail((d) => ({ ...d, [teacherId]: res.data }));
    } catch { toast.error('Failed to load teacher detail'); }
  };

  const toggleExpand = (tid) => {
    if (expandedTeacher === tid) setExpandedTeacher(null);
    else {
      setExpandedTeacher(tid);
      if (!teacherDetail[tid]) fetchTeacherDetail(tid);
    }
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await api.post('/admin/earnings/recompute');
      toast.success(`Credited ${res.data.credited} new class records.`);
      await fetchOverview();
      if (expandedTeacher) await fetchTeacherDetail(expandedTeacher);
    } catch { toast.error('Recompute failed'); }
    finally { setRecomputing(false); }
  };

  const startEditRate = (r) => {
    setEditingRate(r.teacher_id);
    setRateForm({
      paid_india: r.paid_india,
      paid_intl: r.paid_intl,
      group_india: r.group_india,
      group_intl: r.group_intl,
      demo: r.demo,
    });
  };

  const saveRate = async () => {
    try {
      await api.put(`/admin/earnings/rates/${editingRate}`, rateForm);
      toast.success('Rate card updated');
      setEditingRate(null);
      await fetchRates();
      await fetchOverview();
    } catch { toast.error('Failed to update rate'); }
  };

  const saveMilestones = async () => {
    try {
      await api.put('/admin/earnings/milestones', { milestones });
      toast.success('Milestones updated');
      setMilestoneOpen(false);
      await fetchOverview();
    } catch { toast.error('Failed to update milestones'); }
  };

  const submitAdj = async () => {
    if (!adjForm.teacher_id || !adjForm.label || !adjForm.amount_inr) {
      toast.error('All fields required');
      return;
    }
    try {
      await api.post('/admin/earnings/manual-adjustment', { ...adjForm, month, amount_inr: Number(adjForm.amount_inr) });
      toast.success('Adjustment added');
      setAdjOpen(false);
      setAdjForm({ teacher_id: '', month: '', type: 'incentive', label: '', amount_inr: 0, note: '' });
      await fetchOverview();
      if (expandedTeacher) await fetchTeacherDetail(expandedTeacher);
    } catch { toast.error('Failed to add adjustment'); }
  };

  const deleteAdj = async (id) => {
    if (!window.confirm('Delete this adjustment?')) return;
    try {
      await api.delete(`/admin/earnings/manual-adjustment/${id}`);
      toast.success('Deleted');
      await fetchOverview();
      if (expandedTeacher) await fetchTeacherDetail(expandedTeacher);
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Teacher Earnings (Admin)</h1>
          <p className="text-xs text-slate-500 mt-1">All amounts in <strong>INR</strong>. Earnings are credited only after class completion <strong>and</strong> feedback.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-44" data-testid="admin-earnings-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={handleRecompute} disabled={recomputing} data-testid="admin-earnings-recompute">
            <RefreshCw className={`w-4 h-4 mr-1 ${recomputing ? 'animate-spin' : ''}`} /> Recompute
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-earnings-overview">Overview</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-earnings-rates">Rate Cards</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-earnings-milestones">Milestones</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {/* Headline */}
          <div className="my-4 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 p-5 text-white shadow-lg flex items-center justify-between">
            <div>
              <div className="text-blue-100 text-xs">Total Payout — {monthLabel(month)}</div>
              <div className="text-3xl sm:text-4xl font-bold mt-1" data-testid="admin-grand-total">
                {fmtINR(overview?.grand_total_inr)}
              </div>
              <div className="text-blue-100 text-xs mt-1">{(overview?.teachers || []).length} teachers</div>
            </div>
            <Sparkles className="w-12 h-12 text-blue-200 opacity-60" />
          </div>

          {loading && <div className="text-slate-500 text-sm">Loading…</div>}

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Teacher</TableHead>
                  <TableHead className="text-center">Classes</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Group</TableHead>
                  <TableHead className="text-right">Incentives</TableHead>
                  <TableHead className="text-right">Bonus</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(overview?.teachers || []).map((t) => (
                  <React.Fragment key={t.teacher_id}>
                    <TableRow className="hover:bg-slate-50/60" data-testid={`admin-earnings-row-${t.teacher_id}`}>
                      <TableCell>
                        <div className="font-semibold text-slate-900">{t.name}</div>
                        <div className="text-[11px] text-slate-500">{t.tid} · {t.currency}</div>
                      </TableCell>
                      <TableCell className="text-center">{t.classes_total}</TableCell>
                      <TableCell className="text-right">{fmtINR(t.paid_classes_total_inr)}</TableCell>
                      <TableCell className="text-right">{fmtINR(t.group_classes_total_inr)}</TableCell>
                      <TableCell className="text-right text-emerald-700">{fmtINR(t.incentives_total_inr)}</TableCell>
                      <TableCell className="text-right text-amber-700">{fmtINR(t.bonuses_total_inr)}</TableCell>
                      <TableCell className="text-right text-red-600">−{fmtINR(t.deductions_total_inr)}</TableCell>
                      <TableCell className="text-right font-bold text-slate-900">{fmtINR(t.grand_total_inr)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => toggleExpand(t.teacher_id)} data-testid={`admin-earnings-expand-${t.teacher_id}`}>
                            {expandedTeacher === t.teacher_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => { setAdjForm({ teacher_id: t.teacher_id, month, type: 'incentive', label: '', amount_inr: 0, note: '' }); setAdjOpen(true); }} data-testid={`admin-earnings-add-adj-${t.teacher_id}`}>
                            <Plus className="w-3 h-3 mr-1" /> Adjust
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedTeacher === t.teacher_id && teacherDetail[t.teacher_id] && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={9}>
                          <TeacherDetailPanel detail={teacherDetail[t.teacher_id]} onDeleteAdj={deleteAdj} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {(overview?.teachers || []).length === 0 && !loading && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">No teachers found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="rates">
          <div className="my-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Teacher</TableHead>
                  <TableHead className="text-right">Paid (India)</TableHead>
                  <TableHead className="text-right">Paid (Intl)</TableHead>
                  <TableHead className="text-right">Group (India)</TableHead>
                  <TableHead className="text-right">Group (Intl)</TableHead>
                  <TableHead className="text-right">Demo</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.map((r) => (
                  <TableRow key={r.teacher_id}>
                    <TableCell>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[11px] text-slate-500">{r.tid}</div>
                    </TableCell>
                    {editingRate === r.teacher_id ? (
                      <>
                        <TableCell className="text-right"><Input type="number" value={rateForm.paid_india} onChange={e => setRateForm({ ...rateForm, paid_india: Number(e.target.value) })} className="w-24 inline-block text-right" /></TableCell>
                        <TableCell className="text-right"><Input type="number" value={rateForm.paid_intl} onChange={e => setRateForm({ ...rateForm, paid_intl: Number(e.target.value) })} className="w-24 inline-block text-right" /></TableCell>
                        <TableCell className="text-right"><Input type="number" value={rateForm.group_india} onChange={e => setRateForm({ ...rateForm, group_india: Number(e.target.value) })} className="w-24 inline-block text-right" /></TableCell>
                        <TableCell className="text-right"><Input type="number" value={rateForm.group_intl} onChange={e => setRateForm({ ...rateForm, group_intl: Number(e.target.value) })} className="w-24 inline-block text-right" /></TableCell>
                        <TableCell className="text-right"><Input type="number" value={rateForm.demo} onChange={e => setRateForm({ ...rateForm, demo: Number(e.target.value) })} className="w-24 inline-block text-right" /></TableCell>
                        <TableCell className="text-center">
                          <Button size="sm" onClick={saveRate} data-testid={`save-rate-${r.teacher_id}`}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingRate(null)}>Cancel</Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="text-right">{fmtINR(r.paid_india)}</TableCell>
                        <TableCell className="text-right">{fmtINR(r.paid_intl)}</TableCell>
                        <TableCell className="text-right">{fmtINR(r.group_india)}</TableCell>
                        <TableCell className="text-right">{fmtINR(r.group_intl)}</TableCell>
                        <TableCell className="text-right">{fmtINR(r.demo)}</TableCell>
                        <TableCell className="text-center">
                          <Button size="sm" variant="ghost" onClick={() => startEditRate(r)} data-testid={`edit-rate-${r.teacher_id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="milestones">
          <div className="my-4 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Auto-Incentive Milestones</h3>
              <Button size="sm" variant="outline" onClick={() => setMilestones([...milestones, { id: `ms_${Date.now()}`, threshold: 0, amount: 0, label: 'New Milestone' }])}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Milestone
              </Button>
            </div>
            <div className="space-y-2">
              {milestones.map((m, idx) => (
                <div key={m.id} className="flex items-center gap-2">
                  <Input value={m.label} onChange={e => { const ms = [...milestones]; ms[idx] = { ...m, label: e.target.value }; setMilestones(ms); }} placeholder="Label" className="flex-1" />
                  <Input type="number" value={m.threshold} onChange={e => { const ms = [...milestones]; ms[idx] = { ...m, threshold: Number(e.target.value) }; setMilestones(ms); }} placeholder="Classes" className="w-28" />
                  <Input type="number" value={m.amount} onChange={e => { const ms = [...milestones]; ms[idx] = { ...m, amount: Number(e.target.value) }; setMilestones(ms); }} placeholder="₹ Amount" className="w-32" />
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setMilestones(milestones.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Button size="sm" onClick={saveMilestones} data-testid="save-milestones">Save Milestones</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Manual adjustment dialog */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Add Manual Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={adjForm.type} onValueChange={v => setAdjForm({ ...adjForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incentive">Incentive</SelectItem>
                  <SelectItem value="bonus">Additional Bonus</SelectItem>
                  <SelectItem value="deduction">Deduction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Label</Label><Input value={adjForm.label} onChange={e => setAdjForm({ ...adjForm, label: e.target.value })} placeholder="e.g., Performance Bonus" /></div>
            <div><Label>Amount (₹)</Label><Input type="number" value={adjForm.amount_inr} onChange={e => setAdjForm({ ...adjForm, amount_inr: e.target.value })} /></div>
            <div><Label>Note (optional)</Label><Input value={adjForm.note} onChange={e => setAdjForm({ ...adjForm, note: e.target.value })} /></div>
            <Button onClick={submitAdj} className="w-full" data-testid="submit-adjustment">Add</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeacherDetailPanel({ detail, onDeleteAdj }) {
  const byCat = detail.by_category || {};
  return (
    <div className="p-3 space-y-3 text-sm">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Paid (India)" count={byCat.paid_india?.count || 0} amount={byCat.paid_india?.total_inr || 0} />
        <Stat label="Paid (Intl)" count={byCat.paid_intl?.count || 0} amount={byCat.paid_intl?.total_inr || 0} />
        <Stat label="Group (India)" count={byCat.group_india?.count || 0} amount={byCat.group_india?.total_inr || 0} />
        <Stat label="Group (Intl)" count={byCat.group_intl?.count || 0} amount={byCat.group_intl?.total_inr || 0} />
      </div>

      <Section title="Auto Incentives" items={detail.auto_incentives || []} render={(i) => <span><Award className="w-3 h-3 inline text-emerald-500 mr-1" />{i.label} <span className="text-xs text-slate-400">({i.threshold} classes)</span></span>} />
      <Section title="Manual Incentives" items={detail.manual_incentives || []} render={(i) => i.label} onDelete={(id) => onDeleteAdj(id)} />
      <Section title="Bonuses" items={detail.manual_bonuses || []} render={(i) => i.label} onDelete={(id) => onDeleteAdj(id)} />
      <Section title="Deductions" items={detail.deductions || []} render={(i) => i.label} negative onDelete={(id) => onDeleteAdj(id)} />
    </div>
  );
}

function Stat({ label, count, amount }) {
  return (
    <div className="bg-white rounded-lg border p-2">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-semibold">{fmtINR(amount)}</div>
      <div className="text-[10px] text-slate-400">{count} classes</div>
    </div>
  );
}

function Section({ title, items, render, negative = false, onDelete }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-slate-600 mb-1">{title}</div>
      <div className="space-y-1">
        {items.map((i) => (
          <div key={i.id} className="flex items-center justify-between bg-white rounded-md p-2 border">
            <div>{render(i)}</div>
            <div className="flex items-center gap-2">
              <span className={negative ? 'text-red-600 font-semibold' : 'text-emerald-700 font-semibold'}>{negative ? '−' : ''}{fmtINR(i.amount_inr || i.amount_local || 0)}</span>
              {onDelete && (
                <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => onDelete(i.id)} data-testid={`delete-adj-${i.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
